import { SettingsValueProps } from '../components/settings/types';
import { CheckEnvironment, SupportedEnvironments } from '../utils/environment';
import type { PostLoginModalConfig } from './postLoginModalPresets';
import { MRR_POST_LOGIN_MODALS } from './postLoginModalPresets';

declare global {
  interface Window {
    BRAND_TITLE: string;
    locale: string;
  }
}

export enum BrandCode {
  BVR = 'BVR',
  DRS = 'DRS',
  MRR = 'MRR'
}

export const domainBVR = 'owner.beachviewrentals.com';
export const domainMRR = 'owner.mauiresortrentals.com';

// Owner Portal domain constants for custom authentication flows
export const QA_DOMAIN = 'qa.owner-portal-v2.pages.dev';
export const QA_DOMAIN_SUFFIX = '.owner-portal-v2.pages.dev';

/**
 * Configuration for a video resource displayed in the Owner Portal.
 * Used for educational content like onboarding videos and tutorials.
 */
export interface VideoResourceConfig {
  /** Unique identifier for tracking/analytics */
  id: string;
  /** Display title for the video */
  title: string;
  /** Brief description of what the video covers */
  description: string;
  /**
   * Loom share URL (https://www.loom.com/share/…) or embed URL (…/embed/…).
   * A bare 32-character video id is also accepted.
   */
  videoUrl: string;
  /** Optional thumbnail image URL; if omitted, Loom's default thumbnail is shown */
  thumbnailUrl?: string;
  /** Approximate duration (e.g., "3 min") */
  duration?: string;
}

export interface BrandConfig {
  name: string;
  greeting: string;
  pmUUID: string;
  brandCode: BrandCode;
  backgroundUrl?: string;
  logoUrl: string;
  iconUrl: string;
  logoUrlDark: string;
  iconUrlDark: string;
  /** Optional vertical/stacked logo for login page; falls back to logoUrl when not set */
  loginLogoUrl?: string;
  loginLogoUrlDark?: string;
  themeSettings: SettingsValueProps;
  timezone: string;
  customCssUrl?: string; // Optional, for custom styles if needed
  legacyReports: boolean;
  address: {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
  };
  website: string;
  supportEmail: string;
  supportPhone: string;
  /** Base URL of the NPS survey page; scores link here with search params (like the survey emails) */
  npsSurveyUrl: string;
  supportText?: string;
  locale: string;
  origin: string;
  reportsEnabled: {
    booking: boolean;
    occupancy: boolean;
    revenue: boolean;
  };
  stripePublishableKey: string;
  /**
   * Post-login announcement modals (e.g. timeshare walkthrough). Only set on brands that should
   * show them; omit entirely when there should be no modal.
   */
  postLoginModal?: PostLoginModalConfig[];
  /**
   * Video resources for owner education (tutorials, walkthroughs, onboarding).
   * Displayed on the dashboard and FAQs page when configured.
   */
  videoResources?: VideoResourceConfig[];
  // Timeshare configuration
  timeshare: {
    /** 'Pending CA' requires Contributor Agreement signing, 'VERIFICATION' requires manual verification */
    pendingStageType: 'Pending CA' | 'VERIFICATION';
    /** Email domain for Guest Confirmation submissions (e.g., 'gc@mauiresortrentals.com') */
    gcEmailDomain: string;
    /** Dedicated timeshare support contact name (shown in "Need Help?" popover) */
    supportName: string;
    /** Dedicated timeshare support email */
    supportEmail: string;
    /** Dedicated timeshare support phone */
    supportPhone: string;
  };
}

// Define your branding configurations for different domains here
const domainBrand: Record<string, BrandConfig> = {
  [domainBVR]: {
    name: 'Beachview Rentals',
    greeting: 'Welcome',
    pmUUID: 'e646937f-71b7-40a0-9151-8b6644c3c8bc',
    brandCode: BrandCode.BVR,
    backgroundUrl: '/assets/background/bg_img3.webp',
    logoUrl: '/bvr/logo.svg',
    iconUrl: '/bvr/android-chrome-192x192.png',
    logoUrlDark: '/bvr/logo_dark.svg',
    iconUrlDark: '/bvr/android-chrome-192x192.png',
    loginLogoUrl: '/bvr/logo_stacked.svg',
    loginLogoUrlDark: '/bvr/logo_stacked_dark.svg',
    website: 'https://www.beachviewrentals.com',
    supportEmail: 'owner@beachviewrentals.com',
    npsSurveyUrl: process.env.REACT_APP_BVR_NPS_SURVEY_URL || 'https://survey.beachviewrentals.com/NpsSurvey',
    origin: 'https://owner.beachviewrentals.com/',
    supportPhone: '949-673-2627',
    supportText: '949-375-3311',
    locale: 'Newport Beach',
    timezone: 'America/Los_Angeles',
    legacyReports: false,
    address: {
      streetAddress: '314 Old Newport Blvd',
      city: 'Newport Beach',
      state: 'CA',
      postalCode: '92663'
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false
    },
    reportsEnabled: {
      booking: true,
      occupancy: true,
      revenue: true
    },
    stripePublishableKey: process.env.REACT_APP_BVR_STRIPE_PUBLISHABLE_KEY || '',
    videoResources: [
      {
        id: 'owner-portal-overview',
        title: 'Owner Portal Overview',
        description:
          'Learn how to navigate the Owner Portal and access key features for managing your property.',
        videoUrl: 'https://www.loom.com/share/0ae7d69239c04cd2aa5b085df9407ff6',
        duration: '2 min 31 sec'
      },
      {
        id: 'how-to-create-booking',
        title: 'How to Create a Booking',
        description:
          'Step-by-step guide on creating owner bookings and reservations through the portal.',
        videoUrl: 'https://www.loom.com/share/e8877e27424a43c18ba4014f3e70ef07',
        duration: '51 sec'
      }
    ],
    timeshare: {
      pendingStageType: 'VERIFICATION',
      gcEmailDomain: 'gc@beachviewrentals.com',
      supportName: '',
      supportEmail: '',
      supportPhone: ''
    }
  },
  [domainMRR]: {
    name: 'Maui Resort Rentals',
    greeting: 'Aloha',
    pmUUID: '20c41879-c8ee-4a5a-9f1f-40ed31ebd8c1',
    brandCode: BrandCode.MRR,
    backgroundUrl: '/assets/background/bg_img1_33pct.jpg',
    logoUrl: '/mrr/logo.svg',
    iconUrl: '/logo/logo_small.svg',
    logoUrlDark: '/mrr/logo_dark.svg',
    iconUrlDark: '/logo/logo_small_dark.svg',
    loginLogoUrl: '/mrr/logo_vertical.svg',
    loginLogoUrlDark: '/mrr/logo_vertical_dark.svg',
    website: 'https://www.mauiresortrentals.com',
    supportEmail: 'owner@mauiresortrentals.com',
    npsSurveyUrl: process.env.REACT_APP_MRR_NPS_SURVEY_URL || 'https://survey.mauiresortrentals.com/NpsSurvey',
    origin: 'https://owner.mauiresortrentals.com/',
    supportPhone: '(808) 445-6959',
    locale: 'Maui',
    timezone: 'Pacific/Honolulu',
    legacyReports: true,
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761'
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false
    },
    reportsEnabled: {
      booking: true,
      occupancy: true,
      revenue: true
    },
    stripePublishableKey: process.env.REACT_APP_MRR_STRIPE_PUBLISHABLE_KEY || '',
    videoResources: [
      {
        id: 'owner-portal-overview',
        title: 'Owner Portal Overview',
        description:
          'Learn how to navigate the Owner Portal and access key features for managing your property.',
        videoUrl: 'https://www.loom.com/share/210994bd21254dd2b206962342032a50',
        duration: '4 min'
      },
      {
        id: 'how-to-create-booking',
        title: 'How to Create a Booking',
        description:
          'Step-by-step guide on creating owner bookings and reservations through the portal.',
        videoUrl: 'https://www.loom.com/share/c59556ea0b8c466c85a525a372cbc083',
        duration: '1 min 18 sec'
      }
    ],
    postLoginModal: MRR_POST_LOGIN_MODALS,
    timeshare: {
      pendingStageType: 'Pending CA',
      gcEmailDomain: 'gc@mauiresortrentals.com',
      supportName: 'Larry Ward',
      supportEmail: 'larry@mauiresortrentals.com',
      supportPhone: '1 (808) 240-2378'
    }
  },
  default: {
    name: 'Dream Resorts',
    greeting: 'Aloha',
    pmUUID: 'maui_resort_rentals',
    brandCode: BrandCode.DRS,
    backgroundUrl: '/assets/background/bg_img1_33pct.jpg',
    logoUrl: '/mrr/logo.svg',
    iconUrl: '/logo/logo_small.svg',
    logoUrlDark: '/mrr/logo_dark.svg',
    iconUrlDark: '/logo/logo_small_dark.svg',
    loginLogoUrl: '/mrr/logo_vertical.svg',
    loginLogoUrlDark: '/mrr/logo_vertical_dark.svg',
    website: 'https://www.mauiresortrentals.com',
    supportEmail: 'owner@mauiresortrentals.com',
    npsSurveyUrl: process.env.REACT_APP_DRS_NPS_SURVEY_URL || 'https://survey.mauiresortrentals.com/NpsSurvey',
    origin: 'https://owner.mauiresortrentals.com/',
    supportPhone: '(888) 696-6284',
    locale: 'Maui',
    timezone: 'Pacific/Honolulu',
    legacyReports: true,
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761'
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false
    },
    reportsEnabled: {
      booking: true,
      occupancy: true,
      revenue: true
    },
    stripePublishableKey: process.env.REACT_APP_DRS_STRIPE_PUBLISHABLE_KEY || '',
    timeshare: {
      pendingStageType: 'Pending CA',
      gcEmailDomain: 'gc@dreamresorts.com',
      supportName: '',
      supportEmail: '',
      supportPhone: ''
    }
  }
};

// Get brand from cookie (inline to avoid circular dependency)
function getBrandFromCookieInline(): string | null {
  // Check if we're in development environment
  if (!CheckEnvironment(SupportedEnvironments.Dev)) {
    return null;
  }

  const cookies = document.cookie.split(';');
  const brandCookie = cookies.find((cookie) => cookie.trim().startsWith('dev-brand-override='));

  if (brandCookie) {
    return decodeURIComponent(brandCookie.split('=')[1]);
  }

  return null;
}

// Determine the current hostname based on the new requirements
function determineCurrentHostname(): string {
  // In development, check for cookie-based brand override first
  if (CheckEnvironment(SupportedEnvironments.Dev)) {
    const cookieBrand = getBrandFromCookieInline();
    if (cookieBrand) {
      return cookieBrand;
    }
    // In dev without brand cookie, default to MRR to prevent configuration errors
    // BrandGuard will redirect to brand selector anyway
    console.warn('No brand cookie; defaulting to MRR; will redirect to brand selector');
    return domainMRR;
  }

  const actualHostname = window.location.hostname;

  // If the user's host exactly matches our defined brand URLs, use that
  if (actualHostname === domainBVR || actualHostname === domainMRR) {
    return actualHostname;
  }

  // For production: use actual hostname (no brand cookie logic)
  // For dev environments without brand cookie: use actual hostname
  return actualHostname;
}

const currentHostname = determineCurrentHostname();

// Select the brand configuration based on the current hostname
const brandConfig = domainBrand[currentHostname] || domainBrand.default; // Fallback to 'default' if no match

/**
 * Get all available brand domains (excluding 'default')
 * Useful for development brand selector
 */
export function getAvailableBrands(): string[] {
  return Object.keys(domainBrand).filter((domain) => domain !== 'default');
}

/**
 * Get brand config by domain
 */
export function getBrandConfigByDomain(domain: string): BrandConfig | null {
  return domainBrand[domain] || null;
}

export default brandConfig;
